import React, { useEffect, useState } from 'react';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { ErrorContainer, NotInitializedContainer } from '../FinTechOpsCommonElements';
import styles from './styles.module.scss';
import { useCostMetricsContext } from './CostMetricsContext';
import { Box, Container, Grid, Header, SpaceBetween, TableProps, Toggle } from '@amzn/awsui-components-core';
import { numberFormatter } from 'src/utilities/CommonUtilities';
import { FinOpsTable } from '../fintech-ops-table/FinOpsTable';
import { getColumnDefinitionCostMetricsSummaryContainer } from './CostMetricsDataProcessing';
import { TOP_3_ACCOUNTS_VISIBLE_COLUMNS, TOP_3_PRODUCTS_VISIBLE_COLUMNS } from './CostMetricsConstants';

export const CostMetricsDetailsPage: React.FC = () => {
  const finOpsContext = useFinTechContext();
  const costMetricsContext = useCostMetricsContext();
  const [checked, setChecked] = useState(false);

  // Top 3 Products
  const [top3ProductsColumnDefinition, setTop3ProductsColumnDefinition] = useState<TableProps.ColumnDefinition<any>[]>([]);
  const [top3ProductsData, setTop3ProductsData] = useState<any[]>([]);

  // Top 3 Accounts
  const [top3AccountsColumnDefinition, setTop3AccountsColumnDefinition] = useState<TableProps.ColumnDefinition<any>[]>([]);
  const [top3AccountsData, setTop3AccountsData] = useState<any[]>([]);

  useEffect(() => {
    const currentMonth = costMetricsContext.costMetricsSummaryContainer.currentMonth;
    if (costMetricsContext.costMetricsSummaryContainer.top3Products) {
      const colDef = getColumnDefinitionCostMetricsSummaryContainer('product', currentMonth);
      setTop3ProductsColumnDefinition(colDef);
    }
    setTop3ProductsData(costMetricsContext.costMetricsSummaryContainer.top3Products || []);
  }, [costMetricsContext.costMetricsSummaryContainer.top3Products]);

  useEffect(() => {
    const currentMonth = costMetricsContext.costMetricsSummaryContainer.currentMonth;
    if (costMetricsContext.costMetricsSummaryContainer.top3Accounts) {
      const colDef = getColumnDefinitionCostMetricsSummaryContainer('account', currentMonth);
      setTop3AccountsColumnDefinition(colDef);
    }
    setTop3AccountsData(costMetricsContext.costMetricsSummaryContainer.top3Accounts || []);
  }, [costMetricsContext.costMetricsSummaryContainer.top3Accounts]);

  return (
    <div>
      {finOpsContext?.finTechOpsCostMetricsDataStatus == 'loading' && <LoadingSpinner />}
      {finOpsContext?.finTechOpsCostMetricsDataStatus == 'error' && <ErrorContainer />}
      {finOpsContext?.finTechOpsCostMetricsDataStatus == 'pending' && <NotInitializedContainer />}
      {finOpsContext?.finTechOpsCostMetricsDataStatus == 'finished' && (
        <Grid
          gridDefinition={[
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 4, l: 4, xl: 4 } },
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 8, l: 8, xl: 8 } },
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 12, l: 12, xl: 12 } },
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 12, l: 12, xl: 12 } },
            { colspan: { default: 12, xxs: 12, xs: 12, s: 12, m: 12, l: 12, xl: 12 } }
          ]}
        >
          <Container fitHeight header={<Header variant="h2">{`Total Cost Summary`}</Header>}>
            <Box variant="h4" textAlign="left">
              Trailing 6 months Cost
            </Box>
            <Box variant="h3" textAlign="center">
              {numberFormatter(costMetricsContext.costMetricsSummaryContainer.totalCost, true)}
            </Box>
            <Box variant="h4" textAlign="left">
              Previous Month Cost
            </Box>
            <Box variant="h3" textAlign="center">
              {numberFormatter(costMetricsContext.costMetricsSummaryContainer.previousMonthCost, true)}
            </Box>
            {costMetricsContext.costMetricsSummaryContainer.top3Products.length > 0 ? (
              <div className={styles.CostMetricsDetailsFootPrintContainerTable}>
                <FinOpsTable
                  pageSize={3}
                  emptyMessage="No data to display"
                  data={top3ProductsData}
                  filterProperties={[]}
                  visibleContent={TOP_3_PRODUCTS_VISIBLE_COLUMNS}
                  columnDefinitions={top3ProductsColumnDefinition}
                />
                <FinOpsTable
                  pageSize={3}
                  emptyMessage="No data to display"
                  data={top3AccountsData}
                  filterProperties={[]}
                  visibleContent={TOP_3_ACCOUNTS_VISIBLE_COLUMNS}
                  columnDefinitions={top3AccountsColumnDefinition}
                />
              </div>
            ) : (
              <Box textAlign="center">No Data To Display</Box>
            )}
          </Container>
          <Container
            fitHeight
            header={
              <>
                <SpaceBetween size="m" direction="horizontal" alignItems="center">
                  <Header>Cost Metrics Aggregated Details</Header>
                  <Toggle onChange={({ detail }) => setChecked(detail.checked)} checked={checked}>
                    {checked ? 'Product' : 'Account'}
                  </Toggle>
                </SpaceBetween>
              </>
            }
          >
            {checked ? (
              <div className={styles.CostMetricsAggregatedDetailsTable}>
                <FinOpsTable
                  pageSize={100}
                  emptyMessage="No Product to Display"
                  filterProperties={[]}
                  visibleContent={costMetricsContext.aggregatedCostByProductDetailsTable.visibleColumns}
                  columnDefinitions={costMetricsContext.aggregatedCostByProductDetailsTable.columnData}
                  data={costMetricsContext.aggregatedCostByProductDetailsTable.rowData}
                />
              </div>
            ) : (
              <div className={styles.CostMetricsAggregatedDetailsTable}>
                <FinOpsTable
                  pageSize={100}
                  emptyMessage="No Account to Display"
                  filterProperties={[]}
                  visibleContent={costMetricsContext.aggregatedCostByAccountDetailsTable.visibleColumns}
                  columnDefinitions={costMetricsContext.aggregatedCostByAccountDetailsTable.columnData}
                  data={costMetricsContext.aggregatedCostByAccountDetailsTable.rowData}
                />
              </div>
            )}
          </Container>

          <Container
            fitHeight
            header={
              <>
                <SpaceBetween size="m" direction="horizontal" alignItems="center">
                  <Header>Application Aggregated Details</Header>
                </SpaceBetween>
              </>
            }
          >
            <div className={styles.CostMetricsAggregatedDetailsTable}>
              <FinOpsTable
                pageSize={100}
                emptyMessage="No Application to Display"
                filterProperties={[]}
                visibleContent={costMetricsContext.aggregatedCostByApplicationsDetailsTable.visibleColumns}
                columnDefinitions={costMetricsContext.aggregatedCostByApplicationsDetailsTable.columnData}
                data={costMetricsContext.aggregatedCostByApplicationsDetailsTable.rowData}
              />
            </div>
          </Container>

          <Container fitHeight>
            <FinOpsTable
              pageSize={10}
              stickyHeader={false}
              emptyMessage="No costMetrics data to display"
              filterProperties={[]}
              visibleContent={costMetricsContext?.costMetricsDetailsTable?.visibleColumns}
              columnDefinitions={costMetricsContext?.costMetricsDetailsTable?.columnData}
              data={costMetricsContext?.costMetricsDetailsTable?.rowData}
              headerName="Cost Metrics Details"
            />
          </Container>
        </Grid>
      )}
    </div>
  );
};
